import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  BookUser, User, Mail, MapPin, Calendar, ArrowLeft, 
  Instagram, Youtube, Globe, Music, MessageSquare, 
  Briefcase, Users, Shield, AlertCircle, X, Ban,Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import Button from '../components/Button';
import Card from '../components/Card';
import UserName from '../components/UserName';
import Avatar from '../components/Avatar';
import { useAuthStore } from '../store/authStore';
import { initializeConversation } from '../lib/messaging';
import supabase from '../lib/supabase';

export default function UserProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formattedLocation, setFormattedLocation] = useState<string>('');
  const [communities, setCommunities] = useState<any[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [blockStatus, setBlockStatus] = useState<{
    isBlocked: boolean;
    isBlockedBy: boolean;
  }>({ isBlocked: false, isBlockedBy: false });
  const [isBlockLoading, setIsBlockLoading] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          throw new Error('User ID is required');
        }

        const { data, error: fetchError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error('User not found');
        }

        setUser(data);

        // Format location if available
        if (data.location && data.location_enabled) {
          const parts = data.location.split(',').map(part => part.trim());
          if (parts.length >= 2) {
            const city = parts[0];
            const country = parts[1];
            setFormattedLocation(`${city}, ${country}`);
          } else {
            setFormattedLocation(data.location);
          }
        }

        // Fetch user's communities
        const { data: communitiesData, error: communitiesError } = await supabase
          .from('community_members')
          .select(`
            community:communities (
              id,
              name,
              description,
              cover_image,
              member_count,
              is_private,
              leader:leader_id (
                id,
                full_name,
                display_name,
                verified
              )
            )
          `)
          .eq('user_id', id)
          .order('joined_at', { ascending: false });

        if (communitiesError) {
          throw communitiesError;
        }

        setCommunities(communitiesData.map(item => item.community));
        setLoadingCommunities(false);

        // Check if user is banned
        if (currentUser?.role === 'admin') {
          const { data: banData } = await supabase
            .from('banned_users')
            .select('*')
            .eq('user_id', id)
            .is('expires_at', null)
            .limit(1);

          setIsBanned(banData && banData.length > 0);
        }
      } catch (err: any) {
        console.error('Error fetching user profile:', err);
        setError(err.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [id, currentUser]);

  // Check blocking status between users
  useEffect(() => {
    const checkBlockStatus = async () => {
      if (!currentUser?.id || !id || currentUser.id === id) {
        return;
      }

      try {
        // Check if current user has blocked this user
        const { data: blockedData, error: blockedError } = await supabase
          .from('user_blocks')
          .select('blocker_id')
          .eq('blocker_id', currentUser.id)
          .eq('blocked_id', id)
          .limit(1);

        if (blockedError && blockedError.code !== 'PGRST116') {
          console.error('Error checking if user is blocked:', blockedError);
          return;
        }

        // Check if this user has blocked the current user
        const { data: blockedByData, error: blockedByError } = await supabase
          .from('user_blocks')
          .select('blocker_id')
          .eq('blocker_id', id)
          .eq('blocked_id', currentUser.id)
          .limit(1);

        if (blockedByError && blockedByError.code !== 'PGRST116') {
          console.error('Error checking if blocked by user:', blockedByError);
          return;
        }

        setBlockStatus({
          isBlocked: blockedData && blockedData.length > 0,
          isBlockedBy: blockedByData && blockedByData.length > 0
        });
      } catch (error) {
        console.error('Error checking block status:', error);
      }
    };

    checkBlockStatus();
  }, [currentUser?.id, id]);
  const handleBack = () => {
    navigate(-1);
  };

  const handleSendMessage = async () => {
    if (!currentUser) {
      navigate('/auth', { 
        state: { 
          error: 'Please sign in to send messages',
          redirectTo: window.location.pathname
        }
      });
      return;
    }

    try {
      setMessageError(null);
      // Initialize conversation and get ID
      const conversationId = await initializeConversation(id!);
      
      // Navigate to messages with the conversation open
      navigate(`/messages?id=${conversationId}`);
    } catch (error: any) {
      console.error('Error starting conversation:', error);
      setMessageError(error.message || 'Failed to start conversation');
    }
  };

  const handleBlockToggle = async () => {
    if (!currentUser) {
      navigate('/auth', {
        state: {
          error: 'Please sign in to block users',
          redirectTo: window.location.pathname
        }
      });
      return;
    }

    try {
      setIsBlockLoading(true);
      
      if (blockStatus.isBlocked) {
        // Unblock user
        const { error } = await supabase
          .from('user_blocks')
          .delete()
          .eq('blocker_id', currentUser.id)
          .eq('blocked_id', id);

        if (error) throw error;
        setBlockStatus(prev => ({ ...prev, isBlocked: false }));
      } else {
        // Block user
        const { error } = await supabase
          .from('user_blocks')
          .insert({
            blocker_id: currentUser.id,
            blocked_id: id
          });

        if (error) throw error;
        setBlockStatus(prev => ({ ...prev, isBlocked: true }));
      }
    } catch (error: any) {
      console.error('Error toggling block status:', error);
      setError(`Failed to ${blockStatus.isBlocked ? 'unblock' : 'block'} user. Please try again.`);
    } finally {
      setIsBlockLoading(false);
    }
  };
  const handleReport = async () => {
    if (!currentUser) {
      navigate('/auth', {
        state: {
          error: 'Please sign in to report users',
          redirectTo: window.location.pathname
        }
      });
      return;
    }

    try {
      setIsSubmittingReport(true);
      
      const { error: reportError } = await supabase
        .from('user_reports')
        .insert({
          reporter_id: currentUser.id,
          reported_id: id,
          reason: reportReason.trim()
        });

      if (reportError) throw reportError;

      setReportSuccess(true);
      setTimeout(() => {
        setShowReportModal(false);
        setReportSuccess(false);
        setReportReason('');
      }, 5000);
    } catch (error: any) {
      console.error('Error submitting report:', error);
      setError('Failed to submit report. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };
const SpotifyLogo = () => (
    <svg width="20" height="20" viewBox="0 0 168 168" fill="#401c14">
      <path d="M83.996 0.277C37.747 0.277 0.253 37.77 0.253 84.019C0.253 130.27 37.747 167.76 83.996 167.76C130.25 167.76 167.74 130.27 167.74 84.019C167.74 37.773 130.25 0.281 83.995 0.281L83.996 0.277ZM122.4 121.057C120.9 123.517 117.68 124.297 115.22 122.787C95.558 110.777 70.806 108.057 41.656 114.717C38.847 115.357 36.047 113.597 35.407 110.787C34.764 107.977 36.517 105.177 39.333 104.537C71.233 97.249 98.596 100.387 120.67 113.877C123.13 115.387 123.91 118.597 122.4 121.057ZM132.65 98.255C130.76 101.327 126.74 102.297 123.67 100.407C101.16 86.571 66.847 82.564 40.222 90.646C36.769 91.689 33.122 89.743 32.074 86.296C31.034 82.843 32.981 79.203 36.428 78.153C66.841 68.925 104.65 73.395 130.5 89.28C133.57 91.17 134.54 95.19 132.65 98.26V98.255ZM133.53 74.511C106.54 58.48 62.01 57.006 36.241 64.827C32.103 66.082 27.727 63.746 26.473 59.608C25.219 55.468 27.553 51.095 31.694 49.837C61.275 40.857 110.45 42.592 141.524 61.039C145.254 63.248 146.474 68.055 144.264 71.772C142.064 75.494 137.244 76.721 133.534 74.511H133.53Z"/>
    </svg>
  );
  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-neutral">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-surface p-4 flex flex-col">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleBack}
          className="mb-6 w-24 text-primary bg-secondary-light border-secondary-light hover:bg-primary-light hover:border-primary-light hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Card className="p-6 text-center">
          <User className="w-12 h-12 text-neutral mx-auto mb-4" />
          <h2 className="text-xl font-bold font-dillan text-primary mb-2">
            User Not Found
          </h2>
          <p className="text-neutral mb-4">
            {error || 'The requested user profile could not be found.'}
          </p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/')}
          >
            Return Home
          </Button>
        </Card>
      </div>
    );
  }

  const isCurrentUser = currentUser?.id === user.id;
  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="min-h-screen bg-primary-dark p-4">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleBack}
        className="mb-6 w-24 text-primary bg-secondary-light border-secondary-light hover:bg-primary-light hover:border-primary-light hover:text-primary"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </Button>

      <Card className="relative overflow-hidden bg-neutral flex flex-col mb-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Avatar
              src={user.avatar_url}
              alt={user.display_name || user.full_name}
              size="xl"
            />
          </div>
          <div className="flex-1 mb-4">
            <UserName 
              name={user.display_name || user.full_name}
              verified={user.verified}
              size="lg"
              className="mb-1 text-secondary-light"
            />
            {(user.website || user.social?.instagram || user.social?.youtube || user.social?.spotify) && (
            <div className="flex items-center space-x-4 mb-6">
              {user.website && (
                <a
                  href={user.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-accent transition-colors"
                  title="Website"
                >
                  <Globe className="w-5 h-5" />
                </a>
              )}
              {user.social?.instagram && (
                <a
                  href={`https://instagram.com/${user.social.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-accent transition-colors"
                  title="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {user.social?.youtube && (
                <a
                  href={user.social.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-accent transition-colors"
                  title="YouTube"
                >
                  <Youtube className="w-6 h-6" />
                </a>
              )}
              {user.social?.spotify && (
                <a
                  href={user.social.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-accent transition-colors "
                  title="Spotify"
                >
                  <SpotifyLogo className="w-5 h-5 bg-surface-light"/>
                </a>
              )}
            </div>
          )}
          </div>
        </div>

        <div className="mb-4">
          {user.bio && (
            <div className="flex items-start text-surface-light mb-4">
              <BookUser className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold text-secondary-light">Bio: </span>
                <span className="text-surface-light text-md ">{user.bio}</span>
              </div>
            </div>
          )}
          
          {/* Display Professions */}
          {user.professions && user.professions.length > 0 && (
            <div className="flex items-start text-surface-light mb-4">
              <Briefcase className="w-5 h-5 mr-2 mt-0.5 text-primary flex-shrink-0" />
              <div>
                <span className="font-bold text-secondary-light">Profession(s): </span>
                <span>
                  {user.professions
                    .map((profession) =>
                      profession === 'Other' && user.other_profession
                        ? `Other: ${user.other_profession}`
                        : profession
                    )
                    .join(', ')}
                </span>
              </div>
            </div>
          )}

          {user.location_enabled && formattedLocation && (
            <div className="flex items-center text-surface-light">
              <MapPin className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold text-secondary-light">Location: </span>
                <span>{formattedLocation}</span>
              </div>
            </div>
          )}

          {!isCurrentUser && (
            <>
            {messageError && (
              <div className="mb-4 p-3 rounded-lg bg-secondary-dark bg-opacity-10 text-secondary-dark text-sm flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>{messageError}</span>
              </div>
            )}

            <div className="mt-6 grid grid-cols-3 gap-3">
              {/* Message */}
              <Button
                variant="outline"
                onClick={blockStatus.isBlockedBy ? undefined : handleSendMessage}
                disabled={blockStatus.isBlockedBy}
                className="w-full h-9 px-2 gap-1 justify-center text-[11px] sm:text-xs leading-none whitespace-nowrap rounded-full text-surface-light"
                aria-label="Send message"
              >
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                <span>Message</span>
              </Button>
            
              {/* Block / Unblock */}
              <Button
                variant="outline"
                onClick={handleBlockToggle}
                disabled={isBlockLoading || blockStatus.isBlockedBy}
                className={`w-full h-9 px-2 gap-1 justify-center text-[11px] sm:text-xs leading-none whitespace-nowrap rounded-full
                  ${blockStatus.isBlocked
                    ? 'bg-primary-light text-primary hover:bg-secondary hover:text-surface-light border-primary-light hover:border-secondary'
                    : 'bg-secondary text-surface-light hover:bg-primary-light hover:text-primary border-secondary hover:border-primary-light'
                  }`}
                aria-live="polite"
              >
                {isBlockLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    <span className="sr-only sm:not-sr-only">Loading</span>
                  </>
                ) : (
                  <>
                    <Ban className="w-3.5 h-3.5 shrink-0" />
                    <span>{blockStatus.isBlocked ? 'Unblock' : 'Block'}</span>
                  </>
                )}
              </Button>
            
              {/* Report */}
              <Button
                onClick={() => setShowReportModal(true)}
                disabled={blockStatus.isBlockedBy}
                className="w-full h-9 px-2 gap-1 justify-center text-[11px] sm:text-xs leading-none whitespace-nowrap rounded-full bg-base text-surface-light hover:bg-primary-light hover:text-primary border-base hover:border-primary-light"
                aria-label="Report user"
              >
                <Shield className="w-3.5 h-3.5 shrink-0" />
                <span>Report</span>
              </Button>
            </div>
            </>
          )}

          {isCurrentUser && (
            <div className="flex justify-center items-center mt-6">
                <Button 
                  variant="outline" 
                  className="w-40 text-surface-light text-xs"
                  onClick={() => navigate('/profile')}
                >
                  <User className="w-4 h-4 mr-0" />
                  Edit Profile
                </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-start pt-10 p-4 z-10">
          <div className="bg-neutral rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="bg-base px-6 py-4 flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold !text-surface-light">Report User</h2>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="p-2 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 !text-surface-light" />
                </button>
              </div>
            
              <div className="p-4">
                {reportSuccess ? (
                  <div className="text-center py-4">
                    <Shield className="w-12 h-12 text-surface-light mx-auto mb-2" />
                    <p className="text-surface-light">Thank you for your report. Our team will review your report and take appropriate action.</p>
                  </div>
                ) : (
                  <>
                  
  
                    <textarea
                      value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}
                      className="w-full h-20 px-6 py-3 rounded-full border border-surface-light bg-surface-light text-primary placeholder:text-primary focus:border-surface-light focus:ring-0 focus:outline-none mb-6"
                      placeholder="Explain the reason for reporting this person."
                    />
  
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowReportModal(false)}
                        className="flex-1 text-surface-light"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleReport}
                        disabled={!reportReason.trim() || isSubmittingReport}
                        className="flex-1 text-surface-light"
                      >
                        {isSubmittingReport ? 'Submitting...' : 'Submit'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
        </div>
      )}

      {/* Experience Level */}
      {user.experience_level && (
        <Card className="mb-4 bg-neutral">
          <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Experience</h2>
          <p className="text-surface-light">
            {user.experience_level === 'beginner' && 'Beginner/Starting Out'}
            {user.experience_level === 'intermediate' && 'Intermediate (2-5 years experience)'}
            {user.experience_level === 'professional' && 'Professional (5+ years experience)'}
            {user.experience_level === 'veteran' && 'Industry Veteran (10+ years experience)'}
          </p>
        </Card>
      )}

      

      {/* Goals */}
      {user.goals && (
        <Card className="mb-4 bg-neutral">
          <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Goals</h2>
          <p className="text-surface-light whitespace-pre-line">{user.goals}</p>
        </Card>
      )}

      {/* Communities */}
      <Card className="mb-6 bg-neutral">
        <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Communities</h2>
        {loadingCommunities ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-dark mx-auto"></div>
            <p className="mt-2 text-sm text-primary-dark">Loading communities...</p>
          </div>
        ) : communities.length > 0 ? (
          <div className="space-y-4">
            {communities.map((community) => (
              <Link
                key={community.id}
                to={`/communities/${community.id}`}
                className="block"
              >
                <div className="flex items-start space-x-4 p-4 rounded-xl bg-neutral hover:bg-primary-dark transition-colors">
                  <div className="flex-shrink-0">
                    {community.cover_image ? (
                      <img
                        src={community.cover_image}
                        alt={community.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                        <Users className="w-8 h-8 text-primary opacity-20" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-primary truncate">
                        {community.name}
                      </h3>
                      {community.is_private && (
                        <span className="text-xs font-medium text-primary bg-secondary-light px-2 py-1 rounded-full">
                          Private
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-surface-light mt-1 line-clamp-2">
                      {community.description}
                    </p>
                    <div className="flex items-center mt-2 text-xs text-surface-light">
                      <Users className="w-4 h-4 mr-1" />
                      {community.member_count} members
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <Users className="w-12 h-12 text-primary-dark mx-auto mb-2 opacity-50" />
            <p className="text-primary-dark">Not a member of any communities yet</p>
          </div>
        )}
      </Card>

      {/* Account Info */}
      <Card className="bg-neutral">
        <h2 className="text-xl font-semibold font-dillan text-secondary-light mb-6">Account Info</h2>
        <div className="space-y-4">
          <div className="flex items-center text-surface-light">
            <Calendar className="w-5 h-5 text-accent mr-3" />
            <span>Joined {format(new Date(user.created_at), 'MMMM yyyy')}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}